import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import useNotification from 'antd/es/notification/useNotification';

import { notificationService } from 'containers/AlertWrapper/notificationService';

export type OpNotificationInstance = ReturnType<typeof useNotification>[0];

export const useOpNotificationApi = (
  config?: Parameters<typeof useNotification>[0],
) => useNotification(config);

const OpNotificationContext = createContext<OpNotificationInstance | undefined>(
  undefined,
);

export const OpNotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const intercomOpenCount = useSyncExternalStore(
    notificationService.subscribe,
    notificationService.getIntercomOpenCount,
  );
  const intercomStackHeight = useSyncExternalStore(
    notificationService.subscribe,
    notificationService.getIntercomStackHeight,
  );
  // sit above the rendered intercom stack and reserve a lane for the general
  // stack's Clear all button; the 180px floor covers the first measurement
  const [api, context] = useOpNotificationApi({
    placement: 'bottomRight',
    bottom:
      intercomOpenCount > 0 ? Math.max(180, intercomStackHeight + 80) : 24,
  });

  const contextValue = useMemo(() => api, [api]);

  return (
    <OpNotificationContext.Provider value={contextValue}>
      {context}
      {children}
    </OpNotificationContext.Provider>
  );
};

/**
 * TODO: We will need to start adding wrappers around each hapi call to include
 * classNames from AlertWrapper/AlertWrapper.tsx
 */
export const useOpNotification = (): OpNotificationInstance => {
  const context = useContext(OpNotificationContext);

  if (!context) {
    throw new Error(
      'useNotification must be used within a OpNotificationProvider',
    );
  }

  return context;
};

/**
 * Safe hook that returns the notification API when inside OpNotificationProvider,
 * or undefined when outside (e.g. for AlertWrapper fallback when provider is optional).
 */
export const useOpNotificationOptional = ():
  | OpNotificationInstance
  | undefined => useContext(OpNotificationContext);
